import { Component, NgZone, OnInit, ViewChild } from "@angular/core";
import {
  Validators,
  UntypedFormGroup,
  UntypedFormBuilder,
  AbstractControl,
} from "@angular/forms";
import { UserService } from "../../services/user.service";
import { Plan, User } from "@data/user";
import { LoaderService } from "../../services/loader.service";
import { fadeInOutAnimation, savingButtonAnimation } from "../../animations";
import * as isoCurrencyCodesData from "currency-codes/data";
import { SubmitButtonComponent } from "@app/components/common/submit-button.component";
import { DropzoneConfigInterface } from "ngx-dropzone-wrapper";
import { COUNTRIES, STATES_PROVINCES, LOCALES } from "@data/locale";
import { SupermodalComponent } from "@app/components/common/supermodal.component";
import { Router } from "@angular/router";
import { Angulartics2 } from "angulartics2";
import { FileService } from "@app/services/file.service";
import { CouponService } from "@app/services/coupon.service";

class SettingsFormModel {
  constructor(
    public email: string,
    public language: string,
    public pageFormat: string,
    public image_quality: number,
    public newsletterNotifications: number,
    public adsNotifications: string,
    public isoCurrencyCode: string,
    public name: string,
    public address: string,
    public address2: string,
    public city: string,
    public zip_code: string,
    public state_province: string,
    public iso_country_code: string,
    public locale: string,
    public phone_number: string,
    public logo_url: string,
    public website: string,
    public contact_email: string,
    public domain: string,
    public store_front_access_token: boolean,
    public synchronize_orders: boolean,
    public order_confirmation_email: string,
    public ga4_tracking_id: string
  ) {}

  static newEmptyModel(): SettingsFormModel {
    return new SettingsFormModel(
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null
    );
  }
}

@Component({
  templateUrl: "./settings.component.html",
  animations: [fadeInOutAnimation, savingButtonAnimation],
})
export class SettingsComponent implements OnInit {
  protected hasSelectedView: boolean = false;
  reason: string = "";
  moreReason: string = "";
  cancellationReasons: any[] = [
    { code: null, name: "Select a reason" },
    { code: "too_complicated", name: "Too complicated" },
    { code: "too_expensive", name: "Too expensive" },
    { code: "better_solution", name: "Found a better solution" },
    { code: "no_longer_need_it", name: "No longer need it" },
    { code: "missing_features", name: "Missing features" },
    { code: "poor_customer_support", name: "Poor customer support" },
    { code: "other", name: "Other" },
  ];

  froalaOptions: Object = {
    key: "RB3J4B17C10iB6E5E4H5I2I3A6B6C5C4C-11NGNe1IODMGYNSFKV==",
    toolbarButtons: [
      "fontFamily",
      "fontSize",
      "align",
      "color",
      "|",
      "bold",
      "italic",
      "underline",
      "|",
      "html",
    ],
    iconsTemplate: "font_awesome_5",
    colorsText: [
      "#e6292f",
      "#fd6721",
      "#fff64a",
      "#52d15d",
      "#81b5e5",
      "#4253bd",
      "#6d41a0",
      "#fdcdd5",
      "#b97957",
      "#777777",
      "#ffffff",
      "#000000",
    ],
    colorsBackground: [
      "#e6292f",
      "#fd6721",
      "#fff64a",
      "#52d15d",
      "#81b5e5",
      "#4253bd",
      "#6d41a0",
      "#fdcdd5",
      "#b97957",
      "#777777",
      "#ffffff",
      "#000000",
      "REMOVE",
    ],
    fontSizeSelection: true,
    fontSize: [
      "6",
      "8",
      "9",
      "10",
      "11",
      "12",
      "14",
      "18",
      "24",
      "36",
      "42",
      "64",
      "128",
    ],
    fontSizeUnit: "px",
    colorsStep: 6,
    height: 300,
    lineHeights: {
      Default: "",
      Single: "1",
      "1.1": "1.1",
      "1.2": "1.2",
      "1.3": "1.3",
      "1.4": "1.4",
      "1.5": "1.5",
      "1.6": "1.6",
      "1.7": "1.7",
      "1.8": "1.8",
      Double: "2",
      "2.25": "2.25",
      "2.5": "2.5",
      "2.75": "2.75",
      Triple: "3",
    },
    colorsHEXInput: true,
    charCounterCount: false,
    fontFamilySelection: true,
    imageAllowedTypes: ["jpeg", "jpg", "png"],
    imageMaxSize: 1024 * 1024 * 2, // 2 MB
    imageDefaultWidth: 20,
    imageDefaultDisplay: "inline",
    imageOutputSize: true,
    imageEditButtons: ["imageReplace", "imageAlign", "imageRemove"],
    imageInsertButtons: ["imageBack", "|", "imageUpload", "imageByURL"],
    imageStyles: {},
    placeholderText: `Hi,<br/><br/>Thank you for your purchase. Here is a summary of your order:<br/><br/>[ORDER.DETAILS]<br/><br/>We will contact you shortly with more details about shipping and payment.<br/><br/>Best regards.`,
    tableColors: [
      "#e6292f",
      "#fd6721",
      "#fff64a",
      "#52d15d",
      "#81b5e5",
      "#4253bd",
      "#6d41a0",
      "#fdcdd5",
      "#b97957",
      "#777777",
      "#ffffff",
      "#000000",
    ],
    zIndex: 10000,
    imageUploadToS3: {
      bucket: "elasticbeanstalk-us-east-1-462591887108",
      // Your bucket region.
      region: "s3-us-east-1",
      keyStart: "uploads/",
      params: {
        acl: "public-read", // ACL according to Amazon Documentation.
        AWSAccessKeyId: "AKIAJ26WPZPOLYFRHDRA", // Access Key from Amazon.
        policy: "POLICY_STRING", // Policy string computed in the backend.
        signature: "", // Signature computed in the backend.
      },
    },
    tableStyles: {
      "fr-solid-borders": "Solid Borders",
    },
    tableCellStyles: {
      "fr-solid-borders": "Solid Borders",
    },
    toolbarInline: true,
    toolbarVisibleWithoutSelection: true,
    events: {
      "froalaEditor.contentChanged": (e, editor) => {
        this.zone.run(() => {
          this.settingsFormGroup.patchValue({
            order_confirmation_email: editor.html.get(true),
          });
        });
      },
    },
  };

  dropzoneConfig: DropzoneConfigInterface = {
    url: "api/v2/files",
    maxFilesize: 2,
    acceptedFiles: "image/*",
  };
  countries = COUNTRIES;
  statesProvinces = STATES_PROVINCES;
  locales = LOCALES;
  isoCurrencyCodes = [];
  user: User;
  settingsFormGroup: UntypedFormGroup;
  settingsFormModel: SettingsFormModel = SettingsFormModel.newEmptyModel();
  isNowInStore: boolean = false;
  gaView: any;
  isShopify: boolean = false;
  accessToken: string;
  plan: Plan;
  froalaLoaded: boolean = false;
  neverHadSubscription: boolean = false;
  coupon: any;

  @ViewChild("closeAccountSupermodal")
  closeAccountSupermodal: SupermodalComponent;

  @ViewChild("submitButton")
  submitButton: SubmitButtonComponent;

  constructor(
    private angulartics2: Angulartics2,
    private couponService: CouponService,
    private userService: UserService,
    private formBuilder: UntypedFormBuilder,
    protected loaderService: LoaderService,
    private fileService: FileService,
    private router: Router,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.fileService.getS3Config().then((s3Config) => {
      this.froalaOptions["imageUploadToS3"] = s3Config;
      this.froalaLoaded = true;
    });
    isoCurrencyCodesData.forEach((element) => {
      if (element.countries.length > 0) {
        this.isoCurrencyCodes.push(element);
      }
    });
    this.isoCurrencyCodes.sort(
      (a: { currency: string }, b: { currency: string }) => {
        if (a.currency < b.currency) {
          return -1;
        }
        if (a.currency > b.currency) {
          return 1;
        }

        // names must be equal
        return 0;
      }
    );
    this.loaderService.isLoading = true;
    this.settingsFormGroup = this.formBuilder.group(
      {
        email: [
          this.settingsFormModel.email,
          Validators.required,
          this.shouldBeUniqueValidator.bind(this),
        ],
        language: [
          this.settingsFormModel.language || "en",
          Validators.required,
        ],
        pageFormat: [
          this.settingsFormModel.pageFormat || "Letter",
          Validators.required,
        ],
        image_quality: [
          this.settingsFormModel.image_quality || 60,
          Validators.required,
        ],
        newsletterNotifications: [
          this.settingsFormModel.newsletterNotifications || "daily",
          Validators.required,
        ],
        adsNotifications: [
          this.settingsFormModel.adsNotifications || 1,
          Validators.required,
        ],
        isoCurrencyCode: [this.settingsFormModel.isoCurrencyCode || "USD"],
        ga4_tracking_id: [this.settingsFormModel.ga4_tracking_id || ""],
        name: [this.settingsFormModel.name || ""],
        address: [this.settingsFormModel.address || ""],
        address2: [this.settingsFormModel.address2 || ""],
        state_province: [this.settingsFormModel.state_province || ""],
        iso_country_code: [this.settingsFormModel.iso_country_code || ""],
        locale: [this.settingsFormModel.locale || navigator.language],
        city: [this.settingsFormModel.city || ""],
        zip_code: [this.settingsFormModel.zip_code || ""],
        phone_number: [this.settingsFormModel.phone_number || ""],
        store_front_access_token: [
          this.settingsFormModel.store_front_access_token,
        ],
        synchronize_orders: [this.settingsFormModel.synchronize_orders],
        order_confirmation_email: [
          this.settingsFormModel.order_confirmation_email || "",
        ],
        logo_url: [this.settingsFormModel.logo_url || ""],
        website: [this.settingsFormModel.website || ""],
        contact_email: [this.settingsFormModel.contact_email || ""],
        domain: [this.settingsFormModel.domain || ""],
      },
      {
        updateOn: "blur",
      }
    );
    this.userService.me().then((user) => {
      this.setModel(user);
      this.neverHadSubscription =
        user.subscriptions.filter((subscription) => !subscription.is_trial)
          .length === 0;
      this.plan = user.subscription.plan;
      if (!this.plan.limits.custom_domain) {
        this.settingsFormGroup.get("domain").disable();
      }
      if (user.ga_refresh_token) {
        this.userService.refreshToken().then((user) => {
          this.setModel(user);
        });
      }
    });
  }

  setModel(user) {
    this.loaderService.isLoading = false;
    this.user = user;
    this.isShopify = this.userService.isShopify(user);
    this.isNowInStore = this.userService.isNowInStore;

    let account;
    this.settingsFormGroup.setValue({
      email: user.email,
      language: user.settings.language || "en",
      pageFormat: user.settings.page_format || "Letter",
      image_quality: user.settings.image_quality || 60,
      newsletterNotifications: user.settings.newsletter_notification || true,
      adsNotifications: user.settings.ads_notifications || "W",
      domain: user.settings.domain || "",
      isoCurrencyCode: account
        ? account.details.profile.iso_currency_code
        : "USD",
      name: user.business_profile.name || "",
      address: user.business_profile.address || "",
      ga4_tracking_id: user.settings.ga4_tracking_id || "",
      address2: user.business_profile.address2 || "",
      city: user.business_profile.city || "",
      zip_code: user.business_profile.zip_code || "",
      state_province: user.business_profile.state_province || "",
      iso_country_code: user.business_profile.iso_country_code || "",
      locale: user.settings.locale || navigator.language,
      phone_number: user.business_profile.phone_number || "",
      logo_url: user.business_profile.logo_url || "",
      website: user.business_profile.website || "",
      store_front_access_token: user.settings.store_front_access_token || "",
      synchronize_orders: user.settings.synchronize_orders
        ? user.settings.synchronize_orders
        : false,
      order_confirmation_email: user.settings.order_confirmation_email || "",
      contact_email: user.business_profile.contact_email || "",
    });
    this.gaView = user.settings.ga_view;
    this.hasSelectedView = user.settings.ga_view;
    this.accessToken = user.ga_access_token;
  }

  get locale() {
    return this.settingsFormGroup.get("locale");
  }

  get isoCurrencyCode() {
    return this.settingsFormGroup.get("isoCurrencyCode");
  }

  get language() {
    return this.settingsFormGroup.get("language");
  }

  get pageFormat() {
    return this.settingsFormGroup.get("pageFormat");
  }

  get newsletterNotifications() {
    return this.settingsFormGroup.get("newsletterNotifications");
  }

  get adsNotifications() {
    return this.settingsFormGroup.get("adsNotifications");
  }

  get email() {
    return this.settingsFormGroup.get("email");
  }

  onSubmit() {
    // this.user.email = this.email;
    // this.user.settings.language = this.language;
    // this.user.settings.pageFormat = this.pageFormat;
    // this.user.settings.newsletterNotifications = this.newsletterNotifications;
    // this.user.settings.adsNotifications = this.adsNotifications;
    this.submitButton.status = this.submitButton.SAVING_STATUS;
    let prepareUser = this.prepareUser();
    this.userService
      .update(prepareUser)
      .then((user) => {
        this.submitButton.status = this.submitButton.SAVED_STATUS;
        this.coupon = user.stripe_coupon_id;
        this.setModel(user);
      })
      .catch((e) => {
        this.submitButton.status = this.submitButton.FAILED_STATUS;
      });
  }

  save() {
    let prepareUser = this.prepareUser();
    this.userService.update(prepareUser).then((user) => {
      window.location.reload();
      // this.setModel(user);
    });
  }
  prepareUser(): User {
    const formModel = this.settingsFormGroup.value;
    const saveUser: User = {
      email: formModel.email as string,
      settings: {
        language: formModel.language as string,
        page_format: formModel.pageFormat as string,
        image_quality: formModel.image_quality as number,
        synchronize_orders: formModel.synchronize_orders as boolean,
        order_confirmation_email: formModel.order_confirmation_email as string,
        newsletter_notifications: formModel.newsletterNotifications as boolean,
        ads_notifications: formModel.adsNotifications as string,
        store_front_access_token: formModel.store_front_access_token as string,
        ga4_tracking_id: formModel.ga4_tracking_id as string,
        locale: formModel.locale as string,
        ga_view: this.gaView,
        domain: formModel.domain as string,
      },
      business_profile: {
        name: formModel.name as string,
        address: formModel.address as string,
        address2: formModel.address2 as string,
        city: formModel.city as string,
        zip_code: formModel.zip_code as string,
        state_province: formModel.state_province as string,
        iso_country_code: formModel.iso_country_code as string,
        phone_number: formModel.phone_number as string,
        logo_url: formModel.logo_url as string,
        website: formModel.website as string,
        contact_email: formModel.contact_email as string,
      },
    };
    if (this.isNowInStore) {
      this.user.accounts[0].details.profile.iso_currency_code =
        formModel.isoCurrencyCode as string;
      saveUser.accounts = this.user.accounts;
    }
    return saveUser;
  }

  shouldBeUniqueValidator(control: AbstractControl) {
    return new Promise((resolve, reject) => {
      this.userService
        .isEmailUnique(this.user.email, control.value)
        .then((isEmailUnique) => {
          return isEmailUnique
            ? resolve(null)
            : resolve({ isEmailUnique: false });
        })
        .catch((error) => {
          console.log("catch is Email unique");
          return resolve({ isEmailUnique: false });
        });
    });
  }

  onUploadSuccess($event) {
    this.settingsFormGroup.patchValue({ logo_url: $event[1].url });
  }

  showCloseAccount() {
    this.closeAccountSupermodal.open();
  }

  generateCouponCode() {
    this.couponService.create(50).then((coupon) => {
      this.coupon = coupon.id;
    });
  }

  closeDeleteConfirmation() {
    this.closeAccountSupermodal.close();
  }

  closeAccount() {
    this.angulartics2.eventTrack.next({ action: "Close Account" });

    this.userService
      .closeAccount(this.reason + ": " + this.moreReason)
      .then(() => {
        this.router.navigate(["/"]);
      });
  }
}
